import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as DocumentHelper from '../../api/document/documentHelper';
import { IGetEnginePreviewResponse } from '../../api/engine/iApiResponse';
import { IPaginatedResponse } from '../../api/httpClient';
import { EStatus } from '../../shared/enum';
import { IEngine } from '../../shared/iProject';
import { route } from '../../route/Index';
import { useNavigate } from 'react-router-dom';
import { IDocument } from '../../shared/iDocument';
import variable from '../../shared/style/variable';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

interface IHelpPanelProps {}

const HelpPanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const HelpPanel = (props: IHelpPanelProps) => {
    let pageSize = 4;

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [documentPreview, setDocumentPreview] = React.useState<IPaginatedResponse<IDocument>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);

    const fetchDocumentPreview = async (page: number) => {
        setIsLoading(true);
        let res = await DocumentHelper.getDocumentPreviewsAsync(page, pageSize);
        setDocumentPreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchDocumentPreview(pageNumber);
    }, [pageNumber]);

    const handleRemoveDocument = (id: number) => {
        Swal.fire({
            title: t('areYouSure'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: variable.green,
            cancelButtonColor: variable.red,
            confirmButtonText: t('yes'),
            cancelButtonText: t('cancel'),
        }).then(async result => {
            if (result.isConfirmed) {
                let res = await DocumentHelper.deleteDocumentAsync(id);
                if (res.status == 200) {
                    fetchDocumentPreview(pageNumber);
                    Swal.fire({
                        title: t('deleted'),
                        text: t('yourFileHasBeenDeleted'),
                        icon: 'success',
                    });
                }
            }
        });
    };

    const handleClickRowFile = (url: string) => (location.href = url);

    return (
        <HelpPanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('document')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeDocument.replace(':param', 'newEngine'))}>
                        {t('addNewDocument')}
                    </Button>
                </Stack>
                {isLoading && <CircularProgress />}
                {!isLoading && documentPreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {documentPreview!.value.map(x => (
                                            <TableRow key={x.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleClickRowFile(x.url)} component='th' scope='row'>
                                                    {x.name}
                                                </TableCell>
                                                <TableCell onClick={() => handleClickRowFile(x.url)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <DeleteIcon onClick={() => handleRemoveDocument(x.id)} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {documentPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={documentPreview?.pageNumber}
                                    count={documentPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && documentPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </HelpPanelContainer>
    );
};

export default HelpPanel;
